import { JSX, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'

import { executeQuery as executePolarisPlusContentQuery } from '@utils/aureus/queries/polarisPlusContent'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import { CarouselItem, CarouselWidgetAPIProps } from '@widgets/Carousel'
import JSONRenderer from '@components/JSONRenderer'

interface BlickPlusSlidesProps {
  fallbackItems: CarouselWidgetAPIProps['items']
}

const getCarouselItems = async (sectionName: 'home' | 'sport') => {
  const responseData =
    (await executePolarisPlusContentQuery({ sectionName })) ?? []

  return (
    responseData.reduce((acc, curr) => {
      acc.push({
        ...curr.teasers.standardVertical,
        articleId: curr.articleID,
        aureusOfferId: curr.aureusOfferId,
      } satisfies CarouselItem)

      return acc
    }, [] as CarouselItem[]) ?? []
  )
}

const SwiperSlide = styled.div`
  width: 300px;
`

const BlickPlusSlides = ({
  fallbackItems,
}: BlickPlusSlidesProps): JSX.Element[] => {
  const { contextId } = usePageMetadata()

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const sectionName = useMemo(
    () => (contextId === '/sport/' ? 'sport' : 'home'),
    [contextId],
  )

  const { data: carouselItems } = useQuery<CarouselItem[]>({
    queryKey: ['blickplus-carousel', sectionName],
    queryFn: () => getCarouselItems(sectionName),
    enabled: hasAureusJsClientScriptInitialized,
    gcTime: 0,
  })

  const itemsToDisplay = useMemo(() => {
    const recommendedItems = carouselItems?.length ? carouselItems : []
    const cookApiItems = fallbackItems.length ? fallbackItems : []
    return [...recommendedItems, ...cookApiItems].slice(0, 3)
  }, [carouselItems, fallbackItems])

  return itemsToDisplay.map((item) => (
    <SwiperSlide key={item.articleId}>
      <JSONRenderer>{item}</JSONRenderer>
    </SwiperSlide>
  ))
}

export default BlickPlusSlides
